import { useCallback, useEffect, useMemo, useState } from 'react';
import { track, identify } from '@voris/analytics';
import { useAuth } from './useAuth';
import { useRaces } from './useRaces';
import { useData, groupByDriverStint, groupByDriver } from './useData';
import { useSessions } from './useSessions';
import { TopBar } from './TopBar';
import { ChartCard } from './ChartCard';
import { AuthPanel } from './AuthPanel';
import { SessionTabs } from './SessionTabs';
import { AdditionalViewsSection } from './AdditionalViewsSection';
import { InsightsSidebar } from './InsightsSidebar';
import { DriverTeamFilter } from './DriverTeamFilter';
import { useDrivers } from './useDrivers';
import {
  isCleanLap,
  isQualifyingSession,
  sessionFastestSeconds,
  isWithin107,
} from './lapFilters';
import { TelemetryOverlay } from './TelemetryOverlay';

const readUrlState = () => {
  const p = new URLSearchParams(window.location.search);
  const drivers = p.get('drivers');
  return {
    race: p.get('race'),
    session: p.get('session'),
    hideSlowLaps: p.get('clean') !== '0',
    selectedDrivers: drivers ? drivers.split(',').filter(Boolean) : [],
  };
};

const writeUrlState = ({ race, session, hideSlowLaps, selectedDrivers }) => {
  const params = new URLSearchParams(window.location.search);
  if (race) params.set('race', race);
  else params.delete('race');
  if (session) params.set('session', session);
  else params.delete('session');
  if (hideSlowLaps) params.delete('clean');
  else params.set('clean', '0');
  if (selectedDrivers && selectedDrivers.length > 0)
    params.set('drivers', selectedDrivers.join(','));
  else params.delete('drivers');
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', next);
};

export const Dashboard = () => {
  const { user, loading, refresh, signOut } = useAuth();
  const { races, error: racesError } = useRaces();

  const initial = useMemo(readUrlState, []);
  const [selectedRace, setSelectedRace] = useState(initial.race);
  const [selectedSession, setSelectedSession] = useState(initial.session);
  const [hideSlowLaps, setHideSlowLaps] = useState(initial.hideSlowLaps);
  const [selectedDrivers, setSelectedDrivers] = useState(initial.selectedDrivers);
  const [authOpen, setAuthOpen] = useState(false);
  const [selectedTelemetryLap, setSelectedTelemetryLap] = useState(null);

  const teaserRace = useMemo(() => races && races[0], [races]);
  const effectiveSlug = selectedRace || teaserRace?.slug;
  const { sessions, latestCode } = useSessions(effectiveSlug);

  const validSessionCodes = useMemo(
    () => new Set(sessions.map((s) => s.code)),
    [sessions],
  );

  useEffect(() => {
    if (sessions.length === 0) return;
    if (selectedSession && validSessionCodes.has(selectedSession)) return;
    setSelectedSession(latestCode);
  }, [sessions, latestCode, selectedSession, validSessionCodes]);

  useEffect(() => {
    writeUrlState({
      race: selectedRace,
      session: selectedSession,
      hideSlowLaps,
      selectedDrivers,
    });
    track('pageview', {
      race: selectedRace,
      session: selectedSession,
      drivers_count: selectedDrivers.length,
    });
  }, [selectedRace, selectedSession, hideSlowLaps, selectedDrivers]);

  useEffect(() => {
    if (user?.username) identify(user.username, { email: user.email });
  }, [user?.username, user?.email]);

  const handleSelectRace = useCallback((slug) => {
    setSelectedRace(slug);
    setSelectedSession(null);
  }, []);

  const handleSelectSession = useCallback((code) => {
    setSelectedSession(code);
  }, []);

  useEffect(() => {
    const onPop = () => {
      const s = readUrlState();
      setSelectedRace(s.race);
      setSelectedSession(s.session);
      setHideSlowLaps(s.hideSlowLaps);
      setSelectedDrivers(s.selectedDrivers);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const { status, data } = useData(effectiveSlug, selectedSession);
  const { drivers: sessionDrivers } = useDrivers(effectiveSlug, selectedSession);

  const availableDrivers = useMemo(() => {
    if (!data?.laps) return [];
    return Array.from(new Set(data.laps.map((l) => l.driver))).sort();
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data?.laps) return data;
    const isQuali = isQualifyingSession(selectedSession);
    const driverSet = new Set(selectedDrivers);
    const passDriver = (l) => driverSet.size === 0 || driverSet.has(l.driver);
    const fastest = isQuali && hideSlowLaps ? sessionFastestSeconds(data.laps) : null;
    const passClean = (l) => {
      if (!hideSlowLaps) return true;
      return isQuali ? isWithin107(l, fastest) : isCleanLap(l);
    };
    const laps = data.laps.filter((l) => passDriver(l) && passClean(l));
    const groups = isQuali ? groupByDriver(laps) : groupByDriverStint(laps);
    return { ...data, laps, groups };
  }, [data, hideSlowLaps, selectedDrivers, selectedSession]);

  const selectedRaceMeta = useMemo(
    () => (races || []).find((r) => r.slug === effectiveSlug),
    [races, effectiveSlug],
  );

  useEffect(() => {
    if (user) setAuthOpen(false);
  }, [user]);

  const handleSignInClick = () => setAuthOpen(true);

  const sessionLabel =
    sessions.find((s) => s.code === selectedSession)?.label || selectedSession;

  const heroAction = (
    <label className="flex items-center gap-1.5 text-[11px] text-f1-muted cursor-pointer select-none">
      <input
        type="checkbox"
        checked={hideSlowLaps}
        onChange={(e) => setHideSlowLaps(e.target.checked)}
        className="accent-f1-red"
      />
      Hide SC / slow laps
    </label>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <TopBar
        races={races}
        selectedRaceSlug={effectiveSlug}
        onSelectRace={handleSelectRace}
        user={user}
        loading={loading}
        onSignInClick={() => setAuthOpen(true)}
        onSignOut={signOut}
      />

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8 flex flex-col gap-6">
        {racesError && (
          <div className="rounded-lg border border-f1-red/40 bg-f1-red/10 text-sm px-4 py-3">
            Failed to load races: {racesError}
          </div>
        )}

        {selectedRaceMeta && (
          <div className="flex flex-col gap-3">
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-f1-text">
                {selectedRaceMeta.season} {selectedRaceMeta.name}
              </h1>
              {selectedRaceMeta.heldOn && (
                <p className="text-xs text-f1-muted mt-0.5">
                  Round {selectedRaceMeta.round} · {selectedRaceMeta.heldOn.slice(0, 10)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <SessionTabs
                sessions={sessions}
                selected={selectedSession}
                onSelect={handleSelectSession}
              />
              {availableDrivers.length > 0 && (
                <DriverTeamFilter
                  availableDrivers={availableDrivers}
                  selectedDrivers={selectedDrivers}
                  onChange={setSelectedDrivers}
                  drivers={sessionDrivers}
                />
              )}
            </div>
          </div>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <ChartCard
            data={filteredData?.groups}
            status={status}
            onLapClick={(lap) => {
              track('lap_viewed', {
                race: effectiveSlug,
                session: selectedSession,
                driver: lap?.driver,
                lap_number: lap?.lap_number,
              });
              setSelectedTelemetryLap(lap);
            }}
            title="Lap pace"
            subtitle={sessionLabel ? `${sessionLabel} · all drivers` : 'All drivers'}
            action={heroAction}
          />

          <InsightsSidebar
            race={selectedRaceMeta}
            session={selectedSession}
            onSignInClick={handleSignInClick}
          />
        </section>

        <AdditionalViewsSection
          race={selectedRaceMeta}
          session={selectedSession}
          laps={filteredData?.laps}
          status={status}
          onSignInClick={handleSignInClick}
          selectedDrivers={selectedDrivers}
          hideSlowLaps={hideSlowLaps}
        />
      </main>

      {authOpen && !user && (
        <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm grid place-items-center p-4">
          <div className="relative">
            <button
              onClick={() => setAuthOpen(false)}
              className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-f1-surface border border-f1-border text-sm hover:border-f1-muted"
              aria-label="Close"
            >
              ×
            </button>
            <AuthPanel onSignedIn={refresh} />
          </div>
        </div>
      )}

      <TelemetryOverlay
        open={Boolean(selectedTelemetryLap)}
        lap={selectedTelemetryLap}
        race={effectiveSlug}
        session={selectedSession}
        drivers={availableDrivers}
        onClose={() => setSelectedTelemetryLap(null)}
        onSignInClick={handleSignInClick}
      />

      <footer className="border-t border-f1-border py-6 text-center text-xs text-f1-muted">
        Data via FastF1. Built on AWS Fargate + CloudFront.
      </footer>
    </div>
  );
};
